import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'https://www.lankastudio.cn/api';
const BILIBILI_SPACE = 'https://space.bilibili.com/1889105339';

function StudioVideos() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingBvid, setPlayingBvid] = useState(null);

  useEffect(() => {
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/studio-videos`);
      if (!res.ok) throw new Error('获取失败');
      const data = await res.json();
      setVideos(data.videos || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.message);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPlay = (n) => {
    if (!n && n !== 0) return '-';
    if (n >= 10000) return (n / 10000).toFixed(1) + '万';
    return String(n);
  };

  const formatDuration = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    const s = parseInt(val, 10);
    if (isNaN(s)) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.bg} />
      <div style={styles.content}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← 返回</button>
        <div style={styles.header}>
          <h1 style={styles.title}>
            <span style={styles.titleIcon}>📺</span>
            工作室视频
          </h1>
          <p style={styles.subtitle}>蓝卡模玩工作室 B站投稿视频</p>
        </div>

        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner} />
            <p>加载中...</p>
          </div>
        ) : error ? (
          <div style={styles.error}>
            <p>❌ {error}</p>
            <button onClick={fetchVideos} style={styles.retryBtn}>重试</button>
          </div>
        ) : videos.length === 0 ? (
          <div style={styles.empty}>
            <span style={styles.emptyIcon}>📭</span>
            <p>暂无视频</p>
          </div>
        ) : (
          <>
            <div style={styles.grid}>
              {videos.map((v) => (
                <div
                  key={v.bvid}
                  style={styles.card}
                  onClick={() => setPlayingBvid(v.bvid)}
                >
                  <div style={styles.thumbWrap}>
                    <img src={v.pic} alt={v.title} style={styles.thumb} />
                    <div style={styles.duration}>{formatDuration(v.length)}</div>
                    <div style={styles.playIcon}>▶</div>
                  </div>
                  <div style={styles.info}>
                    <h3 style={styles.cardTitle} title={v.title}>
                      {v.title.length > 40 ? v.title.slice(0, 40) + '...' : v.title}
                    </h3>
                    <div style={styles.meta}>
                      <span>播放 {formatPlay(v.play)}</span>
                      <span>弹幕 {formatPlay(v.video_review)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.footer}>
              <a href={BILIBILI_SPACE} target="_blank" rel="noopener noreferrer" style={styles.biliLink}>
                前往 B站空间观看更多 →
              </a>
            </div>
          </>
        )}
      </div>

      {playingBvid && (
        <div style={styles.modal} onClick={() => setPlayingBvid(null)}>
          <div style={styles.modalInner} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeBtn} onClick={() => setPlayingBvid(null)}>✕</button>
            <iframe
              src={`https://player.bilibili.com/player.html?bvid=${playingBvid}&high_quality=1`}
              style={styles.player}
              frameBorder="0"
              allowFullScreen
              title="播放"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    backgroundColor: '#0f1115',
  },
  bg: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'linear-gradient(180deg, #1a1d23 0%, #0f1115 100%)',
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    padding: '24px 20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    marginBottom: 24,
  },
  header: {
    textAlign: 'center',
    marginBottom: 32,
  },
  title: {
    fontSize: 32,
    fontWeight: 800,
    color: '#fff',
    marginBottom: 8,
  },
  titleIcon: { marginRight: 8 },
  subtitle: {
    fontSize: 16,
    color: '#9ca3af',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 20,
  },
  card: {
    background: 'rgba(37, 40, 48, 0.9)',
    borderRadius: 12,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  thumbWrap: {
    position: 'relative',
    paddingTop: '56.25%',
    backgroundColor: '#000',
  },
  thumb: {
    position: 'absolute',
    top: 0, left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  duration: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    background: 'rgba(0,0,0,0.8)',
    color: '#fff',
    fontSize: 12,
    padding: '2px 6px',
    borderRadius: 4,
  },
  playIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 48,
    color: 'rgba(255,255,255,0.9)',
    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
  },
  info: { padding: 12 },
  cardTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
    margin: '0 0 8px 0',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  meta: {
    fontSize: 12,
    color: '#9ca3af',
    display: 'flex',
    gap: 16,
  },
  loading: {
    textAlign: 'center',
    padding: 60,
    color: '#9ca3af',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#12B7F5',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  error: {
    textAlign: 'center',
    padding: 60,
    color: '#ef4444',
  },
  retryBtn: {
    marginTop: 16,
    padding: '10px 24px',
    background: '#12B7F5',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    color: '#9ca3af',
  },
  emptyIcon: { fontSize: 48, display: 'block', marginBottom: 16 },
  footer: {
    textAlign: 'center',
    marginTop: 40,
  },
  biliLink: {
    color: '#00A1D6',
    fontSize: 15,
    textDecoration: 'none',
  },
  modal: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalInner: {
    position: 'relative',
    width: '100%',
    maxWidth: 900,
    aspectRatio: '16/9',
  },
  closeBtn: {
    position: 'absolute',
    top: -40,
    right: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#fff',
    fontSize: 18,
    cursor: 'pointer',
  },
  player: {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: 8,
  },
};

export default StudioVideos;
